import { z } from 'zod';
import { IsoDateTime, Uuid } from './common.js';
/**
 * Module `id-checks` (docs/modules/id-checks.md, ADR 0032, spec 8.6.6). Owners: S50 (server), K8 (kiosk step).
 * Data minimisation: by default only the result is stored. No face matching (spec 16 q8: off and not built).
 */
export const IdDocumentType = z.enum(['passport', 'driving_licence', 'national_id']);
/** How the kiosk read the document: the machine-readable zone, on-device text reading, or reception typed it. */
export const IdCheckMethod = z.enum(['mrz', 'ocr', 'manual']);
export const IdCheckOutcome = z.enum(['pass', 'warn', 'refuse', 'skipped']);
/** Group settings: off by default (spec 8.6.6). Turning a group on needs the tenant's DPIA confirmation (409 otherwise). */
export const IdChecksGroupSettings = z.object({
    mode: z.enum(['off', 'optional', 'required']).default('off'),
    documentTypes: z.array(IdDocumentType).min(1).max(3).default(['passport', 'driving_licence', 'national_id']),
    /** A failed check (expired, or the name doesn't match): warn reception and let them in, or refuse. */
    onFail: z.enum(['warn', 'refuse']).default('warn'),
    /** Storing the document image is separate and off (ADR 0032). Encrypted; deleted after `imageRetentionDays`. */
    storeImage: z.boolean().default(false),
    imageRetentionDays: z.number().int().min(1).max(90).default(30),
    /** Ask once per this many days for members of repeat groups. 0 = every visit. */
    repeatEveryDays: z.number().int().min(0).max(3650).default(0),
});
/** Tenant: nothing but the result retention rule; the DPIA tick is a separate audited action (IdChecksDpiaConfirm). */
export const IdChecksTenantSettings = z.object({
    /** Delete the yes/no result with the visit (0) or after this many days if sooner. */
    resultRetentionDays: z.number().int().min(0).max(3650).default(0),
});
export const IdChecksDpiaStatus = z.object({
    confirmed: z.boolean(),
    confirmedAt: IsoDateTime.nullable(),
    confirmedByLabel: z.string().nullable(),
    reference: z.string().nullable(),
});
export const IdChecksDpiaConfirm = z.object({
    confirm: z.literal(true),
    /** The tenant's own DPIA reference or document name. */
    reference: z.string().trim().min(1).max(200),
});
/**
 * Kiosk step input (step key `id_check`, kiosk order 250). The kiosk compares the name and the expiry on the device and
 * sends only this. No document number, date of birth, nationality or image bytes are ever sent here (strict object).
 */
export const IdCheckStepInput = z
    .object({
    documentType: IdDocumentType.nullable(),
    method: IdCheckMethod.nullable(),
    expiryOk: z.boolean().nullable(),
    nameMatch: z.boolean().nullable(),
    /** Reception skipped the check (optional mode, or "check by eye" with a reason). */
    skipped: z.boolean().default(false),
    skipReason: z.string().trim().max(200).default(''),
    checkedAt: IsoDateTime,
    /** Only when the group stores images: the id from POST /id-checks/device/images. Otherwise must be null. */
    imageUploadId: Uuid.nullable().default(null),
})
    .strict();
export const IdCheckResult = z.object({
    id: Uuid,
    visitId: Uuid,
    documentType: IdDocumentType.nullable(),
    method: IdCheckMethod.nullable(),
    expiryOk: z.boolean().nullable(),
    nameMatch: z.boolean().nullable(),
    outcome: IdCheckOutcome,
    skipReason: z.string(),
    checkedAt: IsoDateTime,
    hasImage: z.boolean(),
    imageDeleteAfter: IsoDateTime.nullable(),
});
/** Kiosk config section `id-checks` (per group visible at the device's site). */
export const IdChecksKioskSection = z.object({
    dpiaConfirmed: z.boolean(),
    groups: z.array(z.object({
        groupId: Uuid,
        mode: z.enum(['optional', 'required']),
        documentTypes: z.array(IdDocumentType),
        onFail: z.enum(['warn', 'refuse']),
        storeImage: z.boolean(),
        repeatEveryDays: z.number().int(),
    })),
});
/** Signed image link (id_checks.view_image): 60 seconds, never cached. */
export const IdCheckImageLink = z.object({ url: z.string(), expiresAt: IsoDateTime });
/** POST /id-checks/device/images response: the id the step input carries as `imageUploadId`. */
export const IdCheckUploadResult = z.object({ uploadId: Uuid });
/**
 * POST /id-checks/device/refused: the kiosk's own comparison refused a visitor, so no sign-in event follows. The kiosk reports it
 * so the alert still fires. Yes/no answers only (strict): no image, no document text. `attemptId` is made on the device and makes the
 * report safe to send twice.
 */
export const IdCheckRefusedReport = z
    .object({
    attemptId: Uuid,
    groupId: Uuid,
    memberId: Uuid.nullable().default(null),
    documentType: IdDocumentType.nullable().default(null),
    method: IdCheckMethod.nullable().default(null),
    expiryOk: z.boolean().nullable().default(null),
    nameMatch: z.boolean().nullable().default(null),
    checkedAt: IsoDateTime,
})
    .strict();
export const IdCheckRefusedResult = z.object({ recorded: z.boolean() });
//# sourceMappingURL=id-checks.js.map